import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { IconApple } from "src/components/icons-mui/social/apple";
import { IconFacebook } from "src/components/icons-mui/social/facebook";
import { IconGoogle } from "src/components/icons-mui/social/google";
import { Button } from "src/components-mui/base/button/button";
import { createThirdPartyAuthUrl } from "src/constants/urls";
import { useAuthModalStore } from "src/stores/auth-modal-store";
import { IGlobal, EnumProviders } from "src/types/layout";
import { getPathnameFromRouter } from "src/utils/url/url";

/** Where in the app is used ThirdPartyAuthButtons, used for tracking */
export enum EnumPlacement {
    HeaderForHome = "header-for-home",
    RegisterModal = "register-modal",
    RegisterModule = "register-module",
    LoginOverview = "login-overview",
}

export interface IThirdPartyAuthButtonsProps {
    loginProviders: IGlobal["loginProviders"];
    placement: EnumPlacement;
    isLoginLabel?: boolean;
    width?: string;
}

export interface IProviderConfigValues {
    icon: JSX.Element;
    labelLogin: string;
    labelRegister: string;
    width?: string;
}

export const providerConfig = {
    [EnumProviders.Facebook]: {
        icon: <IconFacebook sx={{ width: 24, height: 24, mr: 1 }} />,
        labelLogin: "login-with-fb",
        labelRegister: "register-with-fb",
    },
    [EnumProviders.Google]: {
        icon: <IconGoogle sx={{ width: 24, height: 24, mr: 1 }} />,
        labelLogin: "login-with-google",
        labelRegister: "register-with-google",
    },
    [EnumProviders.Apple]: {
        icon: <IconApple sx={{ width: 24, height: 24, mr: 1 }} />,
        labelLogin: "login-with-apple",
        labelRegister: "register-with-apple",
    },
} as const satisfies Record<EnumProviders, IProviderConfigValues>;

export const ThirdPartyAuthButtons = ({ isLoginLabel = false, width, placement, loginProviders }: IThirdPartyAuthButtonsProps) => {
    const router = useRouter();
    const { t } = useTranslation("common");
    const { returnUrl } = useAuthModalStore();

    return (
        <>
            {loginProviders.map(loginProvider => {
                if (!providerConfig[loginProvider]) {
                    return null;
                }

                const { icon, labelLogin, labelRegister } = providerConfig[loginProvider];

                return (
                    <Button
                        key={loginProvider}
                        component="a"
                        href={createThirdPartyAuthUrl(loginProvider, getPathnameFromRouter(router), returnUrl)}
                        variant="outlined"
                        startIcon={icon}
                        className="gtm-register"
                        data-gtm-action={`${placement}-${loginProvider.toLowerCase()}`}
                        sx={width ? { width } : undefined}
                    >
                        {isLoginLabel ? t(labelLogin) : t(labelRegister)}
                    </Button>
                );
            })}
        </>
    );
};
